import axios from '../config/axiosConfig';

const API_URL = '/campaigns/';

// Cache for campaign data
const campaignCache = {
  data: null,
  timestamp: null,
  ttl: 5 * 60 * 1000 // 5 minutes
};

// Helper function to check if cache is valid
const isCacheValid = () => {
  return campaignCache.data && 
         campaignCache.timestamp && 
         (Date.now() - campaignCache.timestamp < campaignCache.ttl);
};

// Get all campaigns with caching
const getCampaigns = async (forceRefresh = false) => {
  try {
    if (!forceRefresh && isCacheValid()) {
      return campaignCache.data;
    }
    
    const response = await axios.get(API_URL);
    campaignCache.data = response.data;
    campaignCache.timestamp = Date.now();
    return response.data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

// Get campaigns by project
const getCampaignsByProject = async (projectId) => {
  try {
    const response = await axios.get(`${API_URL}project/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project campaigns:', error);
    throw error;
  }
};

// Get single campaign with full details
const getCampaign = async (id) => {
  try {
    const response = await axios.get(`${API_URL}${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

// Create campaign
const createCampaign = async (campaignData) => {
  try {
    const response = await axios.post(API_URL, campaignData);
    // Invalidate cache
    campaignCache.data = null;
    return response.data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

// Update campaign
const updateCampaign = async (id, campaignData) => {
  try {
    const response = await axios.put(`${API_URL}${id}`, campaignData);
    // Invalidate cache
    campaignCache.data = null;
    return response.data;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

// Delete campaign
const deleteCampaign = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}${id}`);
    // Invalidate cache
    campaignCache.data = null;
    return response.data;
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

// Add action to campaign
const addActionToCampaign = async (campaignId, actionId) => {
  try {
    const response = await axios.post(`${API_URL}${campaignId}/actions`, { actionId });
    return response.data;
  } catch (error) {
    console.error('Error adding action to campaign:', error);
    throw error;
  }
};

// Remove action from campaign
const removeActionFromCampaign = async (campaignId, actionId) => {
  try {
    const response = await axios.delete(`${API_URL}${campaignId}/actions/${actionId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing action from campaign:', error);
    throw error;
  }
};

// Auto-detect campaigns for a project
const autoDetectCampaigns = async (projectId, options = {}) => {
  try {
    const { timeWindow = 4 * 60 * 60 * 1000 } = options; // Default 4 hours
    const response = await axios.post(`${API_URL}project/${projectId}/detect`, { timeWindow });
    return response.data;
  } catch (error) {
    console.error('Error auto-detecting campaigns:', error);
    throw error;
  }
};

// Get campaign statistics for a project
const getCampaignStats = async (projectId) => {
  try {
    const response = await axios.get(`${API_URL}project/${projectId}/stats`);
    return response.data;
  } catch (error) {
    console.error('Error fetching campaign stats:', error);
    throw error;
  }
};

// Batch create campaigns (for auto-detected campaigns)
const batchCreateCampaigns = async (campaigns) => {
  try {
    const promises = campaigns.map(campaign => createCampaign(campaign));
    const results = await Promise.allSettled(promises);
    
    const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
    const failed = results.filter(r => r.status === 'rejected').map(r => r.reason);
    
    return {
      successful,
      failed,
      totalCreated: successful.length,
      totalFailed: failed.length
    };
  } catch (error) {
    console.error('Error batch creating campaigns:', error);
    throw error;
  }
};

// Get campaigns by status
const getCampaignsByStatus = async (status) => {
  try {
    const allCampaigns = await getCampaigns();
    return allCampaigns.filter(campaign => campaign.status === status);
  } catch (error) {
    console.error('Error filtering campaigns by status:', error);
    throw error;
  }
};

// Get recent campaigns (last N days)
const getRecentCampaigns = async (days = 7) => {
  try {
    const allCampaigns = await getCampaigns();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return allCampaigns.filter(campaign => {
      const campaignDate = new Date(campaign.createdAt);
      return campaignDate >= cutoffDate;
    });
  } catch (error) {
    console.error('Error fetching recent campaigns:', error);
    throw error;
  }
};

// Calculate campaign risk score
const calculateCampaignRisk = (campaign) => {
  let riskScore = 0;
  
  // Base risk on campaign objective
  if (campaign.objective?.toLowerCase().includes('ransomware') || 
      campaign.objective?.toLowerCase().includes('destructive')) {
    riskScore += 30;
  }
  
  // Risk based on number of actions
  if (campaign.actions?.length > 50) riskScore += 20;
  else if (campaign.actions?.length > 20) riskScore += 10;
  
  // Risk based on campaign duration
  if (campaign.startTime && campaign.endTime) {
    const duration = new Date(campaign.endTime) - new Date(campaign.startTime);
    const hours = duration / (1000 * 60 * 60);
    if (hours > 48) riskScore += 15;
    else if (hours > 24) riskScore += 10;
  }
  
  // Risk based on success rate (if statistics available)
  if (campaign.statistics?.successRate > 80) riskScore += 25;
  else if (campaign.statistics?.successRate > 50) riskScore += 15;
  
  return Math.min(10, Math.round(riskScore / 10));
};

// Get campaign summary for executive dashboard
const getCampaignSummary = async () => {
  try {
    const campaigns = await getCampaigns();
    
    const summary = {
      total: campaigns.length,
      active: campaigns.filter(c => c.status === 'active').length,
      completed: campaigns.filter(c => c.status === 'completed').length,
      planned: campaigns.filter(c => c.status === 'planned').length,
      autoDetected: campaigns.filter(c => c.isAutoDetected).length,
      highRisk: campaigns.filter(c => calculateCampaignRisk(c) >= 7).length,
      recentCampaigns: campaigns.filter(c => {
        const date = new Date(c.createdAt);
        const daysDiff = (Date.now() - date) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      }).length
    };
    
    return summary;
  } catch (error) {
    console.error('Error generating campaign summary:', error);
    throw error;
  }
};

// Generate playbook from campaign techniques
const generatePlaybook = async (campaignId, options = {}) => {
  try {
    const response = await axios.post(`${API_URL}${campaignId}/generate-playbook`, options);
    return response.data;
  } catch (error) {
    console.error('Error generating playbook:', error);
    throw error;
  }
};

// Get campaign coverage data
const getCoverage = async (campaignId) => {
  try {
    const response = await axios.get(`${API_URL}${campaignId}/coverage`);
    return response.data;
  } catch (error) {
    console.error('Error fetching coverage:', error);
    throw error;
  }
};

// Clear cache
const clearCache = () => {
  campaignCache.data = null;
  campaignCache.timestamp = null;
};

const campaignService = {
  getCampaigns,
  getCampaignsByProject,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  addActionToCampaign,
  removeActionFromCampaign,
  autoDetectCampaigns,
  getCampaignStats,
  batchCreateCampaigns,
  getCampaignsByStatus,
  getRecentCampaigns,
  calculateCampaignRisk,
  getCampaignSummary,
  generatePlaybook,
  getCoverage,
  clearCache
};

export default campaignService;